import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEO from '@/components/feature/SEO';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { articles } from '@/mocks/data';

export default function Article() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-20 md:pt-24 min-h-screen flex items-center justify-center bg-background-50">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
              <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-2">
              文章未找到
            </h1>
            <p className="text-foreground-500 text-sm mb-6">
              您访问的文章可能已被删除或链接有误
            </p>
            <Link
              to="/"
              className="whitespace-nowrap cursor-pointer inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors"
            >
              <i className="ri-home-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.id !== article.id && a.category === article.category)
    .slice(0, 3);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.publishDate,
    publisher: {
      '@type': 'Organization',
      name: '糖心视频',
      url: 'https://www.tangxinshipin.com/',
    },
    url: `https://www.tangxinshipin.com/article/${article.slug}`,
  };

  return (
    <>
      <SEO
        title={`${article.title} - 糖心视频`}
        description={article.excerpt}
        keywords={article.tags.join(', ') + ', 糖心视频'}
        canonicalUrl={`https://www.tangxinshipin.com/article/${article.slug}`}
        ogType="article"
        schemaData={articleSchema}
      />

      <Navbar />

      <main className="pt-20 md:pt-24">
        <div className="w-full px-4 md:px-6 py-4 bg-background-50">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '分类浏览', href: '/category' },
              { label: article.categoryName, href: `/category?tab=${article.category}` },
              { label: article.title },
            ]}
          />
        </div>

        <article className="py-8 md:py-12 bg-background-50">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-3xl mx-auto">
              <div className="mb-6">
                <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium mb-4">
                  {article.categoryName}
                </span>
                <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-4 leading-tight">
                  {article.title}
                </h1>
                <div className="flex flex-wrap items-center gap-4 text-foreground-500 text-sm">
                  <span className="flex items-center gap-1.5">
                    <i className="ri-user-line"></i>
                    {article.author}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-calendar-line"></i>
                    {article.publishDate}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-eye-line"></i>
                    {article.views.toLocaleString()} 次阅读
                  </span>
                </div>
              </div>

              <div className="relative h-56 md:h-80 rounded-xl overflow-hidden mb-8">
                <img
                  src={article.imageUrl}
                  alt={article.title}
                  className="w-full h-full object-cover object-top"
                />
              </div>

              <div
                className="prose-custom text-foreground-700 leading-relaxed"
                dangerouslySetInnerHTML={{
                  __html: article.content
                    .replace(/<h4>/g, '<h4 class="font-heading text-lg md:text-xl font-bold text-foreground-900 mt-8 mb-3">')
                    .replace(/<p>/g, '<p class="text-sm md:text-base mb-4 leading-relaxed">'),
                }}
              />

              <div className="mt-10 pt-6 border-t border-background-200">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm text-foreground-500">标签：</span>
                  {article.tags.map((tag) => (
                    <span
                      key={tag}
                      className="inline-block px-3 py-1 rounded-full bg-background-100 text-foreground-600 text-xs"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="py-10 md:py-14 bg-background-100">
            <div className="w-full px-4 md:px-6">
              <div className="max-w-6xl mx-auto">
                <h3 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-8">
                  相关推荐
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
                  {relatedArticles.map((ra) => (
                    <Link
                      key={ra.id}
                      to={`/article/${ra.slug}`}
                      className="group cursor-pointer bg-background-50 rounded-xl overflow-hidden transition-all duration-300 hover:-translate-y-1"
                    >
                      <div className="relative h-40 overflow-hidden">
                        <img
                          src={ra.imageUrl}
                          alt={ra.title}
                          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-110"
                        />
                      </div>
                      <div className="p-4">
                        <h4 className="font-heading text-sm md:text-base font-bold text-foreground-900 mb-2 group-hover:text-primary-500 transition-colors line-clamp-2">
                          {ra.title}
                        </h4>
                        <div className="flex items-center justify-between text-xs text-foreground-400">
                          <span>{ra.publishDate}</span>
                          <span className="flex items-center gap-1">
                            <i className="ri-eye-line"></i>
                            {ra.views.toLocaleString()}
                          </span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}